import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, User, LogOut, BookOpen, LayoutDashboard, Settings, FileText } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import api from '../lib/api';
import RetreatHomeBanner from './RetreatHomeBanner';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const isAdmin = user?.role === 'admin';
  const [invoicesEnabled, setInvoicesEnabled] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get('/user/invoices-config').then(r => setInvoicesEnabled(r.data?.invoices_visible === true)).catch(() => {});
  }, [isAuthenticated]);

  // Logo SVG - Esfera con A (Versión 3.3 - Sin palito horizontal)
  const LogoSVG = ({ className = "w-10 h-10" }) => (
    <svg viewBox="0 0 100 100" className={className} fill="none">
      <defs>
        <linearGradient id="goldLogo" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#e8d5a3"/>
          <stop offset="50%" stopColor="#c9a227"/>
          <stop offset="100%" stopColor="#b8922a"/>
        </linearGradient>
        <radialGradient id="glowLogo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#c9a227" stopOpacity="0.3"/>
          <stop offset="100%" stopColor="#c9a227" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id="lightPoint" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1"/>
          <stop offset="40%" stopColor="#e8d5a3" stopOpacity="0.8"/>
          <stop offset="100%" stopColor="#c9a227" stopOpacity="0"/>
        </radialGradient>
      </defs>
      {/* Outer glow */}
      <circle cx="50" cy="50" r="38" fill="url(#glowLogo)"/>
      {/* Golden filled circle */}
      <circle cx="50" cy="50" r="28" fill="url(#goldLogo)"/>
      {/* Elegant A without crossbar - just the two lines */}
      <path d="M38 65 L50 30 L62 65" stroke="#f8f4e8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      {/* Radiating light point at top */}
      <circle cx="50" cy="30" r="8" fill="url(#lightPoint)"/>
      <circle cx="50" cy="30" r="4" fill="#ffffff"/>
    </svg>
  );

  const navLinks = [
    { path: '/quien-soy', label: 'Quién soy' },
    { path: '/campus-interioridad', label: 'Campus' },
    { path: '/viajes', label: 'Viajes' },
    { path: '/formaciones', label: 'Formaciones' },
    { path: '/publicaciones', label: 'Publicaciones' },
    { path: '/metodologia', label: 'Metodología' },
    { path: '/faq', label: 'Preguntas frecuentes' },
    { path: '/contacto', label: 'Contacto' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <RetreatHomeBanner />
      <nav className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group flex-shrink-0"
            data-testid="navbar-logo"
          >
            <LogoSVG className="w-9 h-9 lg:w-10 lg:h-10" />
            <span className="font-heading text-base lg:text-lg text-[hsl(var(--text-primary))] hidden md:block whitespace-nowrap">
              Escuela Akáshica
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`text-sm font-medium transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-[hsl(var(--accent))]'
                    : 'text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Section */}
          <div className="hidden lg:flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 hover:bg-[hsl(var(--sage-light))]"
                    data-testid="user-menu-trigger"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isAdmin ? 'bg-[hsl(var(--accent))]' : 'bg-[hsl(var(--sage))]'}`}>
                      <User className="w-4 h-4 text-white" strokeWidth={1.5} />
                    </div>
                    <span className="text-sm font-medium text-[hsl(var(--text-primary))]">
                      {user?.name?.split(' ')[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {isAdmin && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2" data-testid="menu-admin">
                          <Settings className="w-4 h-4" strokeWidth={1.5} />
                          Panel Admin
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/mi-camino" className="flex items-center gap-2" data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} />
                      Mi Camino
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/perfil" className="flex items-center gap-2" data-testid="menu-profile">
                      <User className="w-4 h-4" strokeWidth={1.5} />
                      Mi Perfil
                    </Link>
                  </DropdownMenuItem>
                  {invoicesEnabled && (
                    <DropdownMenuItem asChild>
                      <Link to="/mis-facturas" className="flex items-center gap-2" data-testid="menu-invoices">
                        <FileText className="w-4 h-4" strokeWidth={1.5} />
                        Mis Facturas
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-[hsl(var(--destructive))] cursor-pointer"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]">
                    Acceder
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button className="btn-gold">
                    Comenzar
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-[hsl(var(--text-primary))]"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-6 border-t border-[hsl(var(--border))]">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`text-base font-medium py-2 ${
                    isActive(link.path)
                      ? 'text-[hsl(var(--accent))]'
                      : 'text-[hsl(var(--text-secondary))]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              <div className="border-t border-[hsl(var(--border))] pt-4 mt-2">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/mi-camino"
                      onClick={() => setIsOpen(false)}
                      className="block py-2 text-[hsl(var(--text-primary))] font-medium"
                    >
                      Mi Camino
                    </Link>
                    <Link
                      to="/perfil"
                      onClick={() => setIsOpen(false)}
                      className="block py-2 text-[hsl(var(--text-secondary))]"
                    >
                      Mi Perfil
                    </Link>
                    {invoicesEnabled && (
                      <Link
                        to="/mis-facturas"
                        onClick={() => setIsOpen(false)}
                        className="block py-2 text-[hsl(var(--text-secondary))]"
                        data-testid="mobile-menu-invoices"
                      >
                        Mis Facturas
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsOpen(false);
                      }}
                      className="block py-2 text-[hsl(var(--destructive))] font-medium mt-2"
                    >
                      Cerrar sesión
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-3">
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <Button variant="outline" className="w-full">
                        Acceder
                      </Button>
                    </Link>
                    <Link to="/register" onClick={() => setIsOpen(false)}>
                      <Button className="btn-gold w-full">
                        Comenzar
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
